import { useState } from "react"
import { Button } from './ui/button'
import { Input } from './ui/input'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from './ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { Search, ChevronDown, Check, Play, Facebook, Instagram, Video } from "lucide-react"
import { buckets, savedCards } from "../constant"

export function InspirationModal({ open, onOpenChange, selectedCards, onToggleCard, onConfirm }) {
    const [searchQuery, setSearchQuery] = useState("")
    const [bucketFilter, setBucketFilter] = useState("all")

    const filteredSavedCards = savedCards.filter(card => {
        const matchesSearch = card.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            card.author.toLowerCase().includes(searchQuery.toLowerCase())
        return matchesSearch
    })

    const getPlatformIcon = (platform) => {
        switch (platform) {
            case "facebook":
                return <Facebook className="w-3.5 h-3.5 text-blue-500" />
            case "instagram":
                return <Instagram className="w-3.5 h-3.5 text-pink-500" />
            case "tiktok":
                return <Video className="w-3.5 h-3.5" />
            default:
                return null
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                onClose={() => onOpenChange(false)}
                className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col bg-white dark:bg-[#0a0a0a]"
            >
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                        Select Inspiration
                    </DialogTitle>
                    <DialogDescription className="text-sm text-gray-700 dark:text-gray-400">
                        Choose up to 5 saved ads to inspire the AI.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-3 py-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <Input
                            type="text"
                            placeholder="Search saved ads..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 h-10 text-base rounded-xl bg-white border-gray-200 text-gray-900 placeholder:text-gray-500 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500"
                        />
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                className="h-10 px-4 rounded-xl gap-2 border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                            >
                                {buckets.find(b => b.id === bucketFilter)?.name || "All Buckets"}
                                <ChevronDown className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="rounded-xl bg-white dark:bg-[#111111] dark:border-white/10">
                            {buckets.map((bucket) => (
                                <DropdownMenuItem
                                    key={bucket.id}
                                    onClick={() => setBucketFilter(bucket.id)}
                                    className="rounded-lg text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-white/5"
                                >
                                    {bucket.name}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                    Selected: {selectedCards.length} / 5
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto py-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {filteredSavedCards.map((card) => {
                            const isSelected = selectedCards.includes(card.id)
                            return (
                                <div
                                    key={card.id}
                                    onClick={() => onToggleCard(card.id)}
                                    className={`relative rounded-xl border overflow-hidden cursor-pointer transition-all ${isSelected
                                            ? 'border-orange-500 ring-2 ring-orange-500/30'
                                            : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20'
                                        }`}
                                >
                                    {isSelected && (
                                        <div className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                                            <Check className="w-4 h-4 text-white" />
                                        </div>
                                    )}

                                    <div className="relative aspect-[4/3]">
                                        <img src={card.image} alt={card.title} className="w-full h-full object-cover" />
                                        {card.isVideo && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                                                    <Play className="w-5 h-5 text-gray-900 ml-0.5" />
                                                </div>
                                            </div>
                                        )}
                                        <div className="absolute bottom-2 left-2 w-6 h-6 rounded-full flex items-center justify-center bg-white/90 dark:bg-black/50">
                                            {getPlatformIcon(card.platform)}
                                        </div>
                                    </div>

                                    <div className="p-3 bg-white dark:bg-[#111111]">
                                        <p className="text-[10px] font-medium uppercase tracking-wider truncate text-gray-500">
                                            {card.author}
                                        </p>
                                        <p className="text-sm font-medium line-clamp-2 mt-1 text-gray-900 dark:text-white">
                                            {card.title}
                                        </p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <DialogFooter className="pt-3 border-t border-gray-200 dark:border-white/10">
                    <div className="flex items-center justify-between w-full">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                            Selected: {selectedCards.length} / 5
                        </span>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                className="rounded-xl border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={onConfirm}
                                className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white"
                            >
                                Use Selected Cards
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}